import {Inject, Injectable} from '@angular/core';
import {datadogLogs} from '@datadog/browser-logs';
import {Store} from '@ngrx/store';
import {DateTime} from 'luxon';
// eslint-disable-next-line @nx/enforce-module-boundaries
import packageJson from '../../../../../../../package.json';
import {environment} from 'src/environments/environment';
import {
  CartItem,
  CartItemSpecification,
  PageAttributes,
  ProductAttributes,
  ServiceOffer,
  YsoProduct,
} from '../../../data/model';
import {CommerceErrorInterface} from '../../error-handling';
import {AnalyticsService, PageAttributeExtraInfo} from './analytics.service';

interface SeAnalyticsClient {
  push: (...events: Array<{type: string; data?: unknown}>) => unknown;
}

type WindowWithSeAnalytics = Window & {
  seAnalytics?: SeAnalyticsClient | Array<{type: string; data?: unknown}>;
};

@Injectable({providedIn: 'root'})
export class SportsEngineAnalyticsService extends AnalyticsService {
  private static logsInitialized = false;
  // The base constructor can start a session before instance fields initialize.
  private static readonly service = 'secommerceui';
  private seAnalytics: SeAnalyticsClient;

  constructor(
    @Inject('seAnalytics') private hostAnalytics: SeAnalyticsClient | undefined,
    store: Store,
  ) {
    super(store);
    this.seAnalytics = this.resolveHostAnalytics(hostAnalytics);
  }

  sessionStart(): void {
    const context = this.contextData;
    this.initializeDatadog();

    datadogLogs.setGlobalContext({
      ...this.buildPageAttributes(),
      service: SportsEngineAnalyticsService.service,
      env: environment.env,
      version: packageJson.version,
      host: window.location.href,
    });
    // Store user information in localStorage for cross-app access
    localStorage.setItem(
      'userName',
      context.registrant.firstName + ' ' + context.registrant.lastName,
    );
    localStorage.setItem('userEmail', context.registrant.email || '');
    this.trackCommerceCart();
  }

  pageView(pageName: string | undefined): void {
    const logData: PageAttributes = {
      ...this.buildPageAttributes({depth4: pageName}),
    };
    this.seAnalytics.push({
      type: 'pageView',
      data: this.camelToSnake(this.handleDataType(logData)),
    });
    this.dataDogLog(logData, 'pageView');
  }

  cartAction(cartItem: CartItem, action: string): void {
    const logData: PageAttributes & {products: ProductAttributes[]} = {
      ...this.buildPageAttributes(),
      products: this.buildProductAttributes(cartItem),
    };
    this.seAnalytics.push({
      type: action,
      data: this.camelToSnake(this.handleDataType(logData)),
    });
    this.dataDogLog(logData, action);
  }

  checkoutStep(): void {
    const logData: PageAttributes = {
      ...this.buildPageAttributes({depth4: 'checkout'}),
    };
    this.seAnalytics.push({
      type: 'checkoutStep',
      data: this.camelToSnake(this.handleDataType(logData)),
    });
    this.dataDogLog(logData, 'checkoutStep');
  }

  clickEvent(action: string): void {
    // KJS [2023-12-04] - ignoring the naming-convention rule for event_type prop
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const logData: PageAttributes & {action: string; event_type: number} = {
      ...this.buildPageAttributes(),
      action: action,
      event_type: 8,
    };
    this.seAnalytics.push({
      type: 'seEvent',
      data: this.camelToSnake(this.handleDataType(logData)),
    });
    this.dataDogLog(logData, 'click');
  }

  viewProductDetails(offer: ServiceOffer | YsoProduct): void {
    const logData: PageAttributes & {products: ProductAttributes[]} = {
      products: this.buildProductAttributes(offer),
      ...this.buildPageAttributes({depth4: offer.name}),
    };
    this.seAnalytics.push({
      type: 'viewProductDetails',
      data: this.camelToSnake(this.handleDataType(logData)),
    });
    this.dataDogLog(logData, 'viewProductDetails');
  }

  applicationError(error?: CommerceErrorInterface, action?: string): void {
    this.initializeDatadog();

    const resolvedAction = action || 'applicationError';
    const errorMessage = error?.message || 'Unknown error';
    const errorStack = error?.stack || 'No stack trace';

    const logData: PageAttributes & {
      action: string;
      errorMessage: string;
      host: string;
      service: string;
      stack: string;
    } = {
      ...this.buildPageAttributes(),
      action: resolvedAction,
      errorMessage: errorMessage,
      host: window.location.href,
      service: SportsEngineAnalyticsService.service,
      stack: errorStack,
    };

    datadogLogs.logger.error(
      resolvedAction,
      logData,
      error?.error as Error | undefined,
    );
  }

  trackCommerceCart(): void {
    if (!localStorage.getItem('commerceCartOpen')) {
      const commerceCartOpen = DateTime.utc().toISO();
      const userEmail = localStorage.getItem('userEmail') || '';
      localStorage.setItem('commerceCartOpen', commerceCartOpen);

      datadogLogs.setGlobalContextProperty(
        'commerceCartOpen',
        commerceCartOpen,
      );
      datadogLogs.setGlobalContextProperty('userEmail', userEmail);

      const logData = {
        ...this.buildPageAttributes(),
        commerceCartOpen: commerceCartOpen,
        userEmail: userEmail,
      } as PageAttributes & {commerceCartOpen: string; userEmail: string};

      this.dataDogLog(logData, 'commerceCartOpen');
    }
  }

  // Web components should queue on window.seAnalytics for the host to process.
  // Do not instantiate se-analytics-js here — that loads core and can duplicate host tracking.
  private resolveHostAnalytics(
    hostAnalytics: SeAnalyticsClient | undefined,
  ): SeAnalyticsClient {
    if (typeof hostAnalytics?.push === 'function') {
      return hostAnalytics;
    }

    const win = window as WindowWithSeAnalytics;
    if (typeof win.seAnalytics?.push === 'function') {
      return win.seAnalytics as SeAnalyticsClient;
    }
    win.seAnalytics = [];
    return win.seAnalytics as SeAnalyticsClient;
  }

  private initializeDatadog(): void {
    if (!SportsEngineAnalyticsService.logsInitialized) {
      datadogLogs.init({
        clientToken: 'pubd70900be4bd2f6656b935732d4bcd5a5',
        site: 'datadoghq.com',
        service: SportsEngineAnalyticsService.service,
        env: environment.env,
        version: packageJson.version,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        telemetrySampleRate: 0,
        trackSessionAcrossSubdomains: true,

        useSecureSessionCookie: true,
        usePartitionedCrossSiteSessionCookie: true,
      });
      SportsEngineAnalyticsService.logsInitialized = true;
    }
  }

  private dataDogLog(
    logData: PageAttributes & {action?: string},
    action: string,
  ): void {
    this.initializeDatadog();

    const resolvedAction = logData.action || action;
    datadogLogs.logger.info(resolvedAction, {
      ...logData,
      service: SportsEngineAnalyticsService.service,
      action: resolvedAction,
      host: window.location.href,
    });
  }

  private buildProductAttributes(
    cartItemsOrOfferData: ServiceOffer | CartItem | YsoProduct,
  ): ProductAttributes[] {
    const signup = this.contextData.signups[0];
    const product: ProductAttributes = {
      brand: cartItemsOrOfferData.name || '',
      category:
        cartItemsOrOfferData instanceof ServiceOffer
          ? String(cartItemsOrOfferData.categoryId)
          : '',
      attributes: this.cleanSpecs(cartItemsOrOfferData),
      name: cartItemsOrOfferData.name,
      // [DWB] 2024/02/27 - Assertion IS necessary for data cleanliness

      lineItemId:
        cartItemsOrOfferData instanceof CartItem ? cartItemsOrOfferData.id : '',
      addOnName: cartItemsOrOfferData.name || '',
      surveyParticipantType: signup.participant.type || undefined,
      surveyParticipantId: signup.participant.id || undefined,
      surveyParticipantDob: signup.participant.dob || undefined,
      surveyParticipantGender: signup.participant.gender || undefined,
      price: this.calculatePrice(cartItemsOrOfferData),
      sku:
        cartItemsOrOfferData instanceof CartItem
          ? cartItemsOrOfferData.productId
          : cartItemsOrOfferData.id,
      variant:
        cartItemsOrOfferData instanceof CartItem
          ? cartItemsOrOfferData.variant
          : undefined,
    };
    return [product];
  }

  private buildPageAttributes(
    extraInfo?: PageAttributeExtraInfo,
  ): PageAttributes {
    const context = this.contextData;
    const signup = context.signups[0];
    return {
      depth1: context.analyticsPageAttributes.depth1,
      depth2: context.analyticsPageAttributes.depth2,
      depth3: context.analyticsPageAttributes.depth3,
      depth4: extraInfo?.depth4 || '',
      surveySport: signup.requestDetails.sport,
      surveyType: signup.requestDetails.eventType,
      surveyRegistrantType: context.registrant.type,
      surveyParticipantId: signup.participant.id,
      surveyParticipantType: signup.participant.type,
      bossOrganizationId: context.bossOrganization.id,
      bossOrganizationName: context.bossOrganization.name,
      bossOrganizationAddrState: context.bossOrganization.addrState,
      bossOrganizationSports: signup.requestDetails.sport,
      bossOrganizationLogo: context.bossOrganization.orgLogo,
    };
  }

  private cleanSpecs(
    cartItemsOrOfferData: ServiceOffer | CartItem | YsoProduct,
  ): CartItemSpecification[] | null {
    const specs =
      'specs' in cartItemsOrOfferData ? cartItemsOrOfferData['specs'] : null;
    let transformedSpecs: CartItemSpecification[] | null = null;
    if (specs && specs.length > 0) {
      transformedSpecs = specs.map(spec => {
        if ('optionId' in spec && 'specId' in spec) {
          // Already CartItemSpecification format
          return spec;
        }
        // Transform simple name/value specs to CartItemSpecification format
        return {
          name: spec.name,
          value: spec.value,
          optionId: '',
          specId: '',
          equals: (): boolean => false,
        };
      });
    }
    return transformedSpecs;
  }

  private calculatePrice(
    cartItemsOrOfferData: ServiceOffer | CartItem | YsoProduct,
  ): number {
    let price = 0;
    if (cartItemsOrOfferData instanceof CartItem) {
      price = cartItemsOrOfferData.unitPrice!;
    } else if (
      cartItemsOrOfferData instanceof ServiceOffer &&
      cartItemsOrOfferData.quote !== undefined
    ) {
      price = cartItemsOrOfferData.quote.cost;
    } else {
      price = cartItemsOrOfferData.price;
    }
    return isNaN(price) ? 0 : price;
  }

  private camelToSnake(obj: Record<string, unknown>): Record<string, unknown> {
    const snakeObj: Record<string, unknown> = {};
    for (const key in obj) {
      if (key === 'products') {
        const result = [];
        if (Array.isArray(obj[key])) {
          const products = obj[key] as [];
          for (const product of products) {
            const aux = this.camelToSnake(product);
            result.push(aux);
          }
          snakeObj[key] = result;
        } else {
          snakeObj[key] = obj[key];
        }
      } else if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const snakeKey = key.replace(
          /[A-Z]/g,
          match => `_${match.toLowerCase()}`,
        );
        snakeObj[snakeKey] =
          typeof obj[key] === 'object'
            ? this.camelToSnake(obj[key] as Record<string, unknown>)
            : obj[key];
      }
    }
    return snakeObj;
  }

  private handleDataType(
    logData: PageAttributes | ProductAttributes,
  ): Record<string, unknown> {
    return logData as unknown as Record<string, unknown>;
  }
}
